import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Switch,
  Textarea,
  useToast,
  UseToastOptions,
  VStack,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { States } from '@duke-hq/svc-api-db/schemas/enums/States.schema';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useState } from 'react';

import { Select } from 'chakra-react-select';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { useParams } from 'react-router-dom';
import { availableStates, states } from '@duke-hq/svc-api/lib/states';
import { LocationError } from './LocationError';

interface SiteLocationFormProps {
  id?: string;
  name: string;
  country: string;
  state: States | undefined;
  description?: string;
  available: boolean;
}

const defaultFormState: SiteLocationFormProps = {
  name: '',
  country: 'Australia',
  state: undefined,
  description: '',
  available: true,
};

export function SiteForm({
  site,
  onClose,
}: {
  site?: NonNullable<RouterOutputs['providers']['locations']['getSiteLocation']>;
  onClose?: () => void;
}) {
  const { providerId } = useParams<{ providerId: string }>();

  const {
    handleSubmit,
    register,
    clearErrors,
    getValues,
    reset,
    setError,
    watch,
    control,
    formState: { errors, isSubmitting, isLoading },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: site
      ? {
          name: site.name,
          country: site.country,
          state: site.state,
          description: site.description || undefined,
          available: site.available,
        }
      : {
          ...defaultFormState,
        },
  });

  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const name = watch('name');
  const state = watch('state');

  const [nameToValidate, setNameToValidate] = useState<boolean>(false);

  if (!providerId) {
    return <LocationError message={t`An error occurred, providerId is required.`} />;
  }

  const { refetch: checkNameExistence } = trpcAdmin.providers.locations.searchSiteLocation.useQuery(
    {
      providerId,
      name,
      state,
    },
    {
      enabled: Boolean(nameToValidate),
    },
  );

  const toastMessage: UseToastOptions = {
    title: site ? t`Site location updated successfully.` : t`Site location created successfully.`,
    status: 'success',
    duration: 7000,
    isClosable: true,
  };

  const createSiteLocation = trpcAdmin.providers.locations.createSiteLocation.useMutation({
    onSuccess: () => {
      toast(toastMessage);
      onClose?.();
      reset();
      void utils.providers.locations.listSiteLocationsWithPagination.refetch();
      void utils.providers.locations.listSiteRegionsWithPagination.refetch();
    },
  });

  const updateSiteLocation = trpcAdmin.providers.locations.updateSiteLocation.useMutation({
    onSuccess: () => {
      toast(toastMessage);
      void utils.providers.locations.getSiteLocation.refetch();
      void utils.providers.locations.listSiteLocationsWithPagination.refetch();
    },
    onError: (error) => {
      toast({
        title: error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const validateName = async () => {
    if (!name || !state) {
      return true;
    }

    // Skip validation if site prop is passed and name is unchanged
    if (site && name === site.name) {
      return true;
    }

    setNameToValidate(true);
    const result = await checkNameExistence();
    setNameToValidate(false);

    return !result.data;
  };

  const onSubmit = async (data) => {
    const isNameValid = await validateName();

    if (!isNameValid) {
      setError('name', {
        type: 'manual',
        message: site
          ? `The site already exists in the state of ${states[site.state]}. Please choose a unique name.`
          : 'The site already exists. Please choose a unique name.',
      });

      setNameToValidate(false);
      return;
    }

    if (site?.id) {
      try {
        await updateSiteLocation.mutateAsync({
          providerId,
          id: site.id,
          data,
        });
      } catch {
        // onError has already shown the message
      }
    } else {
      await createSiteLocation.mutateAsync({
        providerId,
        data,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }} noValidate>
      <VStack spacing={4} align="stretch" p={4}>
        <FormControl isRequired isInvalid={!!errors.name}>
          <FormLabel htmlFor="SiteForm-name">
            <Trans>Name</Trans>
          </FormLabel>
          <Input
            id="SiteForm-name"
            autoComplete="off"
            {...register('name', {
              required: t`Name is required`,
              onChange: () => {
                if (errors.name) {
                  clearErrors('name');
                }
              },
            })}
          />
          <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.state} isRequired>
          <FormLabel htmlFor="SiteForm-state">
            <Trans>State</Trans>
          </FormLabel>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <Select
                placeholder={t`Please select`}
                options={availableStates}
                inputId="SiteForm-state"
                value={availableStates.find((option) => option.value === field.value)}
                onChange={(val) => field.onChange(val?.value)}
              />
            )}
            rules={{
              required: t`Please select a state`,
            }}
          />
          <FormErrorMessage>{errors.state?.message}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="SiteForm-description">
            <Trans>Description</Trans>
          </FormLabel>
          <Textarea
            id="SiteForm-description"
            placeholder="Add a description"
            size="sm"
            {...register('description')}
          />
        </FormControl>

        <FormControl w="full">
          <HStack justify="space-between" align="center" w="full">
            <FormLabel
              mb="0"
              htmlFor="SiteForm-available"
              mt="0"
              display="flex"
              alignItems="center"
            >
              <Trans>Available</Trans>
            </FormLabel>
            <Switch
              id="SiteForm-available"
              defaultChecked={site ? site?.available : getValues('available')}
              colorScheme="brand"
              {...register('available')}
            />
          </HStack>
        </FormControl>

        <Flex justify={'flex-end'}>
          <Button
            right={0}
            type="submit"
            isLoading={isSubmitting}
            isDisabled={
              site
                ? updateSiteLocation.isPending || isSubmitting || isLoading
                : createSiteLocation.isPending || isSubmitting || isLoading
            }
          >
            {site ? t`Update site` : t`Add site`}
          </Button>
        </Flex>
      </VStack>
    </form>
  );
}
